import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactDetailsSchema } from "../../schemas/registrationSchemas";
import {
  getRelationshipManagers,
  processStep1,
  verifyExistingEmail,
} from "../../services/api";
import type { ContactDetails } from "../../types/registration";
import { useMutation, useQuery } from "react-query";
import { Button } from "@/components/ui/button";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import WrappedAccordion from "../WrappedAccordion";
import { z } from "zod";
import { toast } from "sonner";
import { Spinner } from "components";

interface ContactDetailsFormProps {
  onSubmit: (data: ContactDetails, loading?: boolean) => void;
  initialData?: Partial<ContactDetails>;
  onNext: () => void;
  defaultCompletedSteps?: Set<number>;
}

// Define accordion steps
enum AccordionStep {
  CONTACT_DETAILS = 1.1,
  RELATIONSHIP_DETAILS = 1.2,
}

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  onSubmit,
  initialData,
  onNext,
  defaultCompletedSteps
}) => {
  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<z.infer<typeof contactDetailsSchema>>({
    resolver: zodResolver(contactDetailsSchema),
    defaultValues: initialData,
    mode: "onChange",
  });

  const email = watch("email");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [currentStep, setCurrentStep] = useState<AccordionStep>(
    AccordionStep.CONTACT_DETAILS
  );
  const [completedSteps, setCompletedSteps] = useState<Set<AccordionStep>>(
    defaultCompletedSteps || new Set()
  );


  // Debounce the email input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(email);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [email]);

  // React Query hook to verify the debounced email
  const { data: verificationResult } = useQuery({
    queryKey: ["verifyEmail", debouncedEmail],
    queryFn: () => verifyExistingEmail(debouncedEmail),
    enabled:
      !!debouncedEmail &&
      z.string().email().safeParse(debouncedEmail).success,
    refetchOnWindowFocus: false,
  });

  const myself = localStorage.getItem("staff_id");

  const { data: relationshipManagers, isLoading: isLoadingManagers } =
    useQuery({
      queryKey: ["relationshipManagers"],
      queryFn: getRelationshipManagers,
      enabled: !!email,
    });

  const rmData = relationshipManagers?.map((rm) => ({
    label:
      rm?.id !== undefined
        ? rm.id === myself
          ? "Myself"
          : rm.fname
        : "",
    value: rm?.id !== undefined ? rm.id : "",
  }));

  const { mutate: mutateStep1, isLoading: isLoadingStep1 } = useMutation({
    mutationFn: processStep1,
    onSuccess: (data) => {
      toast.success("Contact details saved successfully!");
      localStorage.setItem(
        "currentPartnerRegistrationId",
        JSON.stringify(data.id)
      );
      
      // Mark current step as completed and move to next
      setCompletedSteps((prev) => new Set(prev).add(AccordionStep.CONTACT_DETAILS));
      setCurrentStep(AccordionStep.RELATIONSHIP_DETAILS);
      
      onSubmit(getValues(), false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to save contact details"
      );
    },
  });

  const { mutate: mutateStep2, isLoading: isLoadingStep2 } = useMutation({
    mutationFn: processStep1, // Replace with your step 2 API function
    onSuccess: (data) => {
      toast.success("Relationship details saved successfully!");
      
      // Mark step as completed
      setCompletedSteps((prev) => new Set(prev).add(AccordionStep.RELATIONSHIP_DETAILS));
      
      onSubmit(getValues(), false);
      onNext();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to save relationship details"
      );
    },
  });

  const isLoading = isLoadingStep1 || isLoadingStep2;

  const handleStep1Submit = (data: ContactDetails) => {
    const partnerId = new URLSearchParams(window.location.search).get("id");
    const type = new URLSearchParams(window.location.search).get("type");

    mutateStep1({
      email: data.email,
      mobile: data.mobile || "",
      relationshipManager: data.relationshipManager || "",
      sub_step: 1.1,
    });
  };

  const handleStep2Submit = (data: ContactDetails) => {
    mutateStep2({
      email: data.email,
      mobile: data.mobile || "",
      partner_id: localStorage.getItem("currentPartnerRegistrationId") || "",
      relationshipManager: data.relationshipManager || "",
      sub_step: 1.2,
    });
  };

  const getAccordionTitle = (step: AccordionStep, isCompleted: boolean) => {
    const titles = {
      [AccordionStep.CONTACT_DETAILS]: "Contact Details",
      [AccordionStep.RELATIONSHIP_DETAILS]: "Relationship Details",
    };
    
    return (
      <div className="flex items-center justify-between w-full">
        <h2 className="">{titles[step]}</h2>
        {isCompleted && (
          <svg
            className="w-5 h-5 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
    );
  };

  return (
    <div className="w-full mx-auto space-y-6">
      {isLoading && <Spinner />}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Contact Details
        </h2>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {/* Contact Details Accordion */}
        <WrappedAccordion
          open={currentStep === AccordionStep.CONTACT_DETAILS}
          title={getAccordionTitle(
            AccordionStep.CONTACT_DETAILS,
            completedSteps.has(AccordionStep.CONTACT_DETAILS)
          )}
          onToggle={() => {
            if (currentStep !== AccordionStep.CONTACT_DETAILS) {
              setCurrentStep(AccordionStep.CONTACT_DETAILS);
            }
          }}
        >
          <div className="space-y-4 max-w-sm">
            <Input
              {...register("email")}
              label="Email Address"
              type="email"
              placeholder="Enter Email"
              error={errors.email?.message}
              isValid={!errors.email && email?.length > 0}
              required
            />

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Mobile number{" "}
                <span className="text-gray-500">(optional)</span>
              </label>
              <div className="flex">
                <div className="flex items-center bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg px-3">
                  <span className="text-sm text-gray-600">+91</span>
                  <svg
                    className="w-4 h-4 ml-2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                <input
                  {...register("mobile")}
                  className="flex-1 h-12 border border-gray-300 rounded-r-lg px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  placeholder="Enter Mobile number"
                />
              </div>
            </div>

            <Button
              disabled={getValues("email") === "" || isLoadingStep1}
              onClick={handleSubmit(handleStep1Submit)}
              type="button"
              className="bg-blue-700 w-[100px] text-white-A700 rounded-md py-2.5 px-4"
            >
              {isLoadingStep1 ? "Saving..." : "Next"}
            </Button>

            {/* Display verification result dynamically */}
            {verificationResult && verificationResult.name && (
              <div className="rounded-lg p-4">
                <p className="text-sm">
                  Email is associated with the following partner:
                </p>
                <p className="text-base font-semibold text-gray-600 mt-1">
                  {verificationResult.name}
                </p>
                <p className="text-sm mt-1">You may still proceed.</p>
              </div>
            )}
          </div>
        </WrappedAccordion>

        {/* Relationship Details Accordion */}
        <WrappedAccordion
          open={currentStep === AccordionStep.RELATIONSHIP_DETAILS}
          title={getAccordionTitle(
            AccordionStep.RELATIONSHIP_DETAILS,
            completedSteps.has(AccordionStep.RELATIONSHIP_DETAILS)
          )}
          disabled={!completedSteps.has(AccordionStep.CONTACT_DETAILS)}
          onToggle={() => {
            if (
              completedSteps.has(AccordionStep.CONTACT_DETAILS) &&
              currentStep !== AccordionStep.RELATIONSHIP_DETAILS
            ) {
              setCurrentStep(AccordionStep.RELATIONSHIP_DETAILS);
            }
          }}
        >
          <div className="pt-6 space-y-4 max-w-sm">
            <Select
              {...register("relationshipManager")}
              placeholder="Select relationship manager"
              options={rmData || []}
              isLoading={isLoadingManagers}
            />
            <Button
              disabled={!!verificationResult?.name || isLoadingStep2}
              onClick={handleSubmit(handleStep2Submit)}
              type="button"
              className="bg-blue-700 text-white-A700 w-[100px] rounded-md py-2.5 px-4"
            >
              {isLoadingStep2 ? "Saving..." : "Next"}
            </Button>
          </div>
        </WrappedAccordion>
      </form>
    </div>
  );
};